
import { z } from "zod";

// ======================
// BEVA CONSTANTS & ENUMS
// ======================

/**
 * BEVA Location Index values
 */
export const BEVA_LOCATION_INDEX = {
  RURAL: 6,
  AGGLO: 8,
  CENTER: 10
} as const;

export type BevaLocationIndex = typeof BEVA_LOCATION_INDEX[keyof typeof BEVA_LOCATION_INDEX];

/**
 * BEVA Health Index range
 */
export const BEVA_HEALTH_INDEX = {
  MIN: 0,
  MAX: 4
} as const;

/**
 * BEVA Aesthetic Index range
 */
export const BEVA_AESTHETIC_INDEX = {
  MIN: 1,
  MAX: 6
} as const;

/**
 * BEVA Size Index mapping based on circumference
 */
export const BEVA_SIZE_INDEX_MAP = [
  { min: 10, max: 14, index: 0.5 },
  { min: 15, max: 22, index: 0.8 },
  { min: 23, max: 40, index: 1 },
  { min: 41, max: 60, index: 2 },
  { min: 61, max: 80, index: 3 },
  { min: 81, max: 100, index: 4 },
  { min: 101, max: 120, index: 5 },
  { min: 121, max: 140, index: 6 },
  { min: 141, max: 160, index: 7 },
  { min: 161, max: 180, index: 8 },
  { min: 181, max: 200, index: 9 },
  { min: 201, max: 220, index: 10 },
  { min: 221, max: 240, index: 11 },
  { min: 241, max: 260, index: 12 },
  { min: 261, max: 280, index: 13 },
  { min: 281, max: 300, index: 14 },
  { min: 301, max: 320, index: 15 },
  { min: 321, max: 340, index: 16 },
  { min: 341, max: 360, index: 17 },
  { min: 361, max: 380, index: 18 },
  { min: 381, max: 400, index: 19 },
  { min: 401, max: 420, index: 20 },
  { min: 421, max: 440, index: 21 },
  { min: 441, max: 460, index: 22 },
  { min: 461, max: 480, index: 23 },
  { min: 481, max: 500, index: 24 },
  { min: 501, max: 520, index: 25 },
  { min: 521, max: 540, index: 26 },
  { min: 541, max: 560, index: 27 },
  { min: 561, max: 580, index: 28 },
  { min: 581, max: 600, index: 29 },
  { min: 601, max: Infinity, index: 45 }
] as const;

// ======================
// BEVA SCHEMAS
// ======================

/**
 * BEVA Calculation Input Schema
 * Validates input parameters for BEVA calculations
 */
export const BevaCalculationInputSchema = z.object({
  varietyGrade: z.number()
    .min(0, "La note variétale doit être un nombre positif")
    .max(10, "La note variétale ne peut pas dépasser 10"),

  healthIndex: z.number()
    .int("L'indice sanitaire doit être un entier")
    .min(BEVA_HEALTH_INDEX.MIN, `L'indice sanitaire doit être au moins ${BEVA_HEALTH_INDEX.MIN}`)
    .max(BEVA_HEALTH_INDEX.MAX, `L'indice sanitaire ne peut pas dépasser ${BEVA_HEALTH_INDEX.MAX}`),

  aestheticIndex: z.number()
    .int("L'indice esthétique doit être un entier")
    .min(BEVA_AESTHETIC_INDEX.MIN, `L'indice esthétique doit être au moins ${BEVA_AESTHETIC_INDEX.MIN}`)
    .max(BEVA_AESTHETIC_INDEX.MAX, `L'indice esthétique ne peut pas dépasser ${BEVA_AESTHETIC_INDEX.MAX}`),

  locationIndex: z.number()
    .int("L'indice de localisation doit être un entier")
    .refine(
      (val) => Object.values(BEVA_LOCATION_INDEX).includes(val as BevaLocationIndex),
      "L'indice de localisation doit être 6, 8 ou 10"
    ),

  healthColumn: z.number()
    .int("La colonne sanitaire doit être un entier")
    .min(0, "La colonne sanitaire doit être positive")
    .optional(),

  aestheticColumn: z.number()
    .int("La colonne esthétique doit être un entier")
    .min(0, "La colonne esthétique doit être positive")
    .optional()
});

/**
 * BEVA Calculation Result Schema
 * Represents the result of a BEVA calculation
 */
export const BevaCalculationResultSchema = z.object({
  beva: z.number()
    .min(0, "La valeur BEVA doit être positive"),

  message: z.string()
    .min(1, "Le message ne peut pas être vide"),

  id: z.number()
    .int("L'ID doit être un entier")
    .positive("L'ID doit être positif")
    .optional(),

  // Additional calculation details
  sizeIndex: z.number()
    .min(0, "L'indice de taille doit être positif")
    .optional(),

  countSubject: z.number()
    .int("Le nombre de sujets doit être un entier")
    .min(1, "Le nombre de sujets doit être au moins 1")
    .optional(),

  diameter: z.number()
    .positive("Le diamètre doit être positif")
    .optional(),

  circumference: z.number()
    .positive("La circonférence doit être positive")
    .optional()
});

/**
 * BEVA Validation Error Schema
 * Represents validation errors for BEVA calculations
 */
export const BevaValidationErrorSchema = z.object({
  message: z.string()
    .min(1, "Le message d'erreur ne peut pas être vide"),

  errorCode: z.number()
    .int("Le code d'erreur doit être un entier")
    .min(100, "Le code d'erreur doit être au moins 100")
    .max(599, "Le code d'erreur ne peut pas dépasser 599"),

  fieldErrors: z.record(z.string(), z.array(z.string()))
    .optional(),

  details: z.string()
    .optional()
});

/**
 * BEVA API Response Schema
 * Represents the API response structure for BEVA endpoints
 */
export const BevaResponseSchema = z.object({
  data: z.union([
    BevaCalculationResultSchema,
    BevaValidationErrorSchema
  ]),

  statusCode: z.number()
    .int("Le code de statut doit être un entier")
    .min(200, "Le code de statut doit être au moins 200")
    .max(599, "Le code de statut ne peut pas dépasser 599")
});

/**
 * BEVA Calculation Request Schema for Inventaire
 * Specific schema for inventaire BEVA calculations
 */
export const BevaInventaireRequestSchema = z.object({
  inventaireId: z.number()
    .int("L'ID d'inventaire doit être un entier")
    .positive("L'ID d'inventaire doit être positif"),

  data: BevaCalculationInputSchema
});

/**
 * BEVA Calculation Request Schema for Essence
 * Specific schema for essence BEVA calculations
 */
export const BevaEssenceRequestSchema = z.object({
  essenceId: z.number()
    .int("L'ID d'essence doit être un entier")
    .positive("L'ID d'essence doit être positif"),

  data: BevaCalculationInputSchema
});

// ======================
// BEVA TYPES
// ======================

export type BevaCalculationInput = z.infer<typeof BevaCalculationInputSchema>;
export type BevaCalculationResult = z.infer<typeof BevaCalculationResultSchema>;
export type BevaValidationError = z.infer<typeof BevaValidationErrorSchema>;
export type BevaResponse = z.infer<typeof BevaResponseSchema>;
export type BevaInventaireRequest = z.infer<typeof BevaInventaireRequestSchema>;
export type BevaEssenceRequest = z.infer<typeof BevaEssenceRequestSchema>;

// ======================
// BEVA UTILITY FUNCTIONS
// ======================

/**
 * Calculate size index based on diameter
 */
export function calculateSizeIndex(diameter: number): number {
  const circumference = diameter * Math.PI;

  for (const range of BEVA_SIZE_INDEX_MAP) {
    if (circumference >= range.min && circumference <= range.max) {
      return range.index;
    }
  }

  // Fallback to the highest index for very large trees
  const lastIndex = BEVA_SIZE_INDEX_MAP[BEVA_SIZE_INDEX_MAP.length - 1];
  return lastIndex?.index ?? 10; // Default to highest index if array is empty (should never happen)
}

/**
 * Calculate BEVA value using the standard formula
 */
export function calculateBevaValue(
  varietyGrade: number,
  healthIndex: number,
  aestheticIndex: number,
  locationIndex: number,
  diameter: number,
  countSubject: number = 1
): number {
  const sizeIndex = calculateSizeIndex(diameter);
  return (aestheticIndex + healthIndex) * (varietyGrade * locationIndex) * sizeIndex * countSubject;
}

/**
 * Validate BEVA calculation input with detailed error messages
 */
export function validateBevaInput(input: unknown): {
  success: boolean;
  data?: BevaCalculationInput;
  errors?: string[];
} {
  const result = BevaCalculationInputSchema.safeParse(input);

  if (result.success) {
    return {
      success: true,
      data: result.data
    };
  }

  const errors = result.error.issues.map((err) =>
    `${err.path.join('.')}: ${err.message}`
  );

  return {
    success: false,
    errors
  };
}

/**
 * Get location index label
 */
export function getLocationIndexLabel(locationIndex: BevaLocationIndex): string {
  switch (locationIndex) {
    case BEVA_LOCATION_INDEX.RURAL:
      return "Zone rurale";
    case BEVA_LOCATION_INDEX.AGGLO:
      return "Agglomération";
    case BEVA_LOCATION_INDEX.CENTER:
      return "Centre ville";
    default:
      return "Inconnu";
  }
}

/**
 * Get health index description
 */
export function getHealthIndexDescription(healthIndex: number): string {
  const descriptions = [
    "Très mauvais état",
    "Mauvais état",
    "État moyen",
    "Bon état",
    "Très bon état"
  ];

  return descriptions[healthIndex] || "État inconnu";
}

/**
 * Get aesthetic index description
 */
export function getAestheticIndexDescription(aestheticIndex: number): string {
  const descriptions = [
    "", // Index starts at 1
    "Très faible valeur esthétique",
    "Faible valeur esthétique",
    "Valeur esthétique moyenne",
    "Bonne valeur esthétique",
    "Très bonne valeur esthétique",
    "Excellente valeur esthétique"
  ];

  return descriptions[aestheticIndex] || "Valeur esthétique inconnue";
}
